import { useTouchTransform } from "@/hook/useTouchTransform";
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
} from "vue";

import Api from "@/api/userService";
import WebGlManager from "@/webglview/WebGlManager";
import CarPosition from "@/model/CarPosition";
import useConvertMapElement from "@/webglview/ConvertMapElementUtils";
import HeadingPoint from "@/model/HeadingPoint";
import VehicleMapInfo from "@/model/VehicleMapInfo";
import VehiclePosition from "@/model/VehiclePosition";
import MapElement from "@/model/MapElement";
import {
  main_type_functionPoint_3,
  main_type_noisyPoint_2,
  main_type_virtualWall_1,
  main_type_zone_5,
  mapElementsUtil,
} from "@/webglview/MapElementsUtil";
import LocLaneInfo from "@/model/LocLaneInfo";
import LaneInfo from "@/model/LaneInfo";
import CleanTaskingInfo, {
  FloorCompositeTask,
  SubTask,
} from "@/model/CleanTaskingInfo";
import ChildTaskStatus from "@/model/ChildTaskStatus";
import MapBaseInfo from "@/model/MapBaseInfo";
import MapElement2 from "@/model/MapElement2";
import ConstantsType from "@/constant/ConstantsType";
import TaskInfo from "@/model/TaskInfo";

const mapDivId = "mapDivId";

export default defineComponent({
  name: "MapView",
  props: {
    vin: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
    showMapVirtualWalls: {
      //是否允显示地图所有虚拟墙
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    showFunctionPoints: {
      //是否允显示地图所有功能点
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    showFunctionAreas: {
      //是否允显示地图所有功能区域
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    mapId: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
  },
  emits: ["listen-scale", "listen-mapname", "listen-task", "listen-floorname"],
  setup(props, context) {
    const vin = computed<string>(() => props.vin);
    const myMapId = computed<string>(() => props.mapId);
    const mapId = ref<string>("");
    const showMapVirtualWalls = computed<boolean>(
      () => props.showMapVirtualWalls
    );
    const showFunctionPoints = computed<boolean>(
      () => props.showFunctionPoints
    );
    const showFunctionAreas = computed<boolean>(() => props.showFunctionAreas);
    const cleanTaskingInfo = ref<CleanTaskingInfo>();

    const webGlManager = new WebGlManager(mapDivId);
    let laneMap: Map<number, LocLaneInfo>;
    let mapTaskListAll: TaskInfo[];

    const {
      translateX,
      translateY,
      scale,
      rotateAngle,
      setLastScale,
      setMinScale,
      setLastTrans,
      resetTranslate,
      resetTranslateMinScale,
    } = useTouchTransform("monitor_page");

    const {
      getCarPosition2Local,
      getMapElements2Local,
      getLaneInfos2LocalMap,
      setMapPicInfoForConvert,
    } = useConvertMapElement();

    const delMapVirtualWalls = (elements: MapElement2[]) => {
      if (elements?.length > 0) {
        const element = mapElementsUtil(elements, main_type_virtualWall_1);
        const locElements = getMapElements2Local(element);
        webGlManager.setMapVirtualWalls(locElements);
      }
    };

    const delMapNoiseAreas = (elements: MapElement2[]) => {
      if (elements?.length > 0) {
        const element = mapElementsUtil(elements, main_type_noisyPoint_2);
        const locElements = getMapElements2Local(element);
        webGlManager.setMapNoiseAreas(locElements);
      }
    };

    const delFunctionAreas = (elements: MapElement2[]) => {
      if (elements?.length > 0) {
        const element = mapElementsUtil(elements, main_type_zone_5);
        const locElements = getMapElements2Local(element);
        webGlManager.setFunctionAreas(locElements);
      }
    };

    const delFunctionPoints = (elements: MapElement2[]) => {
      if (elements?.length > 0) {
        const element = mapElementsUtil(elements, main_type_functionPoint_3);
        const locElements = getMapElements2Local(element);
        webGlManager.setMapFunctionPoints(locElements);
      }
    };

    const delMapElements = (elements: MapElement2[]) => {
      delMapVirtualWalls(elements);
      delMapNoiseAreas(elements);
      delFunctionAreas(elements);
      delFunctionPoints(elements);
    };

    const setLocLaneInfoMap = (lanes: LaneInfo[]) => {
      laneMap = getLaneInfos2LocalMap(lanes);
    };

    const setLoadMapPicScale = (width: number, height: number) => {
      const div = document.getElementById(mapDivId) as HTMLDivElement;
      const w = div.clientWidth as number;
      const h = div.clientHeight as number;

      let mScaleH = 1;
      let mScaleW = 1;
      let mScale = 1;
      if (height > h) {
        mScaleH = h / height;
      }
      if (width > w) {
        mScaleW = w / width;
      }
      if (mScaleH < mScaleW) {
        mScale = mScaleH;
      } else {
        mScale = mScaleW;
      }
      scale.value = mScale;
      setMinScale(scale.value);
      setLastScale(scale.value);
      console.log(w, h, mScale, "ssssssss");
    };

    const mapPicCallBack = (ev: VehicleMapInfo, mapid: string) => {
      if (ev) {
        context.emit("listen-mapname", ev.result?.map_name);
        context.emit("listen-floorname", ev.result?.floor_name);
        mapTaskListAll = ev.result?.task_list;
        const src = ev.result?.map_url;
        resetTranslate();
        if (src?.length > 0) {
          webGlManager.setMapPicInfo(ev, (mapBaseInfo: MapBaseInfo) => {
            setMapPicInfoForConvert(
              mapBaseInfo.left_boundary,
              mapBaseInfo.down_boundary,
              mapBaseInfo.map_width,
              mapBaseInfo.map_height
            );
            setLoadMapPicScale(mapBaseInfo.map_width, mapBaseInfo.map_height);
            setLocLaneInfoMap(ev.result?.lane_list);
            webGlManager.setScale(scale.value);
            context.emit("listen-scale", scale.value);
            getMyMapElements(mapid);
          });
        }
      }
    };

    const getCurMapDetail = () => {
      if (vin.value && mapId.value) {
        Api.getVehicleMapInfo(vin.value, mapId.value).then((val) => {
          mapPicCallBack(val, mapId.value);
          getVehiclePosition();
        });
      }
    };

    const getMyMapDetail = () => {
      if (vin.value && myMapId.value) {
        Api.getVehicleMapInfo(vin.value, myMapId.value).then((val) => {
          mapPicCallBack(val, myMapId.value);
          getTaskIngInfo();
          if (myMapId.value == mapId.value) {
            getVehiclePosition();
          }
        });
      }
    };

    const getMyMapElements = (mapid: string) => {
      if (vin.value && mapid) {
        Api.getMapElements(vin.value, mapid).then((val) => {
          delMapElements(val);
        });
      }
    };
    const getFallPrevention = () => {
      if (vin.value) {
        Api.getFallPrevention(vin.value).then((val: any) => {
          if (val.result?.prevented == true) {
            setCarInfo(val.xg, val.yg, val.heading);
          }
        });
      }
    };

    const setCarInfo = (x: number, y: number, h: number) => {
      const locCarPosition = getCarPosition2Local(x, y, h);
      if (locCarPosition) {
        webGlManager.setCarPosition(locCarPosition);
      }
    };

    const delCarPosition = (val: VehiclePosition) => {
      const rfidInfo = cleanTaskingInfo.value?.result?.rfid_point;
      if (rfidInfo && typeof rfidInfo?.xg == "number") {
        setCarInfo(
          cleanTaskingInfo.value?.result?.rfid_point?.xg as number,
          cleanTaskingInfo.value?.result?.rfid_point?.yg as number,
          cleanTaskingInfo.value?.result?.rfid_point?.heading as number
        );
      } else {
        setCarInfo(
          val.result?.global_x,
          val.result?.global_y,
          val.result?.heading
        );
      }
    };

    const getVehiclePosition = () => {
      if (vin.value && mapId.value) {
        Api.getVehiclePosition(vin.value).then((val) => {
          delCarPosition(val);
        });
      }
    };

    let vehiclePositionInterval: number;
    const pollVehiclePosition = () => {
      clearInterval(vehiclePositionInterval);
      vehiclePositionInterval = setInterval(() => {
        getVehiclePosition();
      }, 5000);
    };

    const runningLane2LocLane = (runningTask: ChildTaskStatus) => {
      let locLane: LocLaneInfo | undefined;
      if (laneMap?.size > 0) {
        const lane = laneMap.get(runningTask.id);
        if (lane) {
          locLane = {} as LocLaneInfo;
          locLane.lane_id = lane.lane_id;
          locLane.lane_type = runningTask.type;
          locLane.lane_url = runningTask.clean_pic_url;
          locLane.left_up_coordinate = lane.left_up_coordinate;
          return locLane;
        }
      }
      return locLane;
    };

    const subLane2LocLane = (runningTask: SubTask) => {
      let locLane: LocLaneInfo | undefined;
      if (laneMap?.size > 0) {
        const lane = laneMap.get(Number(runningTask.id));
        if (lane) {
          locLane = {} as LocLaneInfo;
          locLane.lane_id = lane.lane_id;
          locLane.lane_type = runningTask.type;
          locLane.lane_url = runningTask.task_pic;
          locLane.left_up_coordinate = lane.left_up_coordinate;
          return locLane;
        }
      }
      return locLane;
    };

    const getRuningLaneInfo = (
      task_list: ChildTaskStatus[],
      laneId: number
    ) => {
      let runningLaneInfo: LocLaneInfo | undefined;
      task_list?.forEach((val) => {
        if (val.id == laneId) {
          runningLaneInfo = runningLane2LocLane(val);
        }
      });
      return runningLaneInfo;
    };

    const setAllRunningLaneMap = (taskList: ChildTaskStatus[]) => {
      if (!webGlManager.checkNeedUpdate(taskList)) {
        taskList?.forEach((val) => {
          const running = runningLane2LocLane(val);
          if (running) {
            webGlManager.setRunningTaskPath(running);
          }
        });
      }
      if (!(taskList?.length > 0)) {
        webGlManager.clearRunningTaskPaths();
      }
    };

    const setAllFloorLaneMap = (taskList: SubTask[]) => {
      if (!webGlManager.checkFloorNeedUpdate(taskList)) {
        taskList?.forEach((val) => {
          const running = subLane2LocLane(val);
          if (running) {
            webGlManager.setRunningTaskPath(running);
          }
        });
      }
      if (!(taskList?.length > 0)) {
        webGlManager.clearRunningTaskPaths();
      }
    };

    const setRunningLaneMap = (taskList: ChildTaskStatus[]) => {
      if (taskList?.length > 0) {
        taskList?.forEach((val) => {
          if (
            val?.type == ConstantsType.task_type_clean_1 &&
            val?.task_status > ConstantsType.task_status_default_0
          ) {
            const running = runningLane2LocLane(val);
            if (running) {
              webGlManager.setRunningTaskPath(running, true);
            }
          }
        });
      }
      if (!(taskList?.length > 0)) {
        webGlManager.clearRunningTaskPaths();
      }
    };

    const setFloorLaneMap = (taskList: SubTask[]) => {
      if (taskList?.length > 0) {
        taskList?.forEach((val) => {
          if (
            val?.type == ConstantsType.task_type_clean_1 &&
            val?.state > ConstantsType.task_status_default_0
          ) {
            const running = subLane2LocLane(val);
            if (running) {
              webGlManager.setRunningTaskPath(running, true);
            }
          }
        });
      }
      if (!(taskList?.length > 0)) {
        webGlManager.clearRunningTaskPaths();
      }
    };

    const getTaskNameForId = (taskId: string | number) => {
      let taskInfo: TaskInfo | null;
      taskInfo = null;
      mapTaskListAll?.forEach((val) => {
        if (taskId + "" == val.task_id + "") {
          taskInfo = val;
        }
      });
      return taskInfo;
    };

    const getMyMapTaskId = (val: CleanTaskingInfo) => {
      //根据选择的mapId获取多楼层任务中的组合任务Id
      let taskId: number | undefined;
      const tempMapId = myMapId.value ? myMapId.value : mapId.value;
      const floorList = val.result?.floor_task_status?.composite_task_list;
      if (floorList?.length > 0) {
        floorList.some((val) => {
          if (val.map_id == tempMapId) {
            taskId = val.task_id;
            return true;
          } else {
            return false;
          }
        });
      }
      return taskId;
    };

    const getMyMapFloorRunTaskInfo = (val: CleanTaskingInfo) => {
      //根据选择的mapId获取多楼层任务中的组合任务Id
      let taskInfo: FloorCompositeTask | undefined;
      const tempMapId = myMapId.value ? myMapId.value : mapId.value;
      const floorList = val.result?.floor_task_status?.composite_task_list;
      if (floorList?.length > 0) {
        floorList.some((val) => {
          if (val.map_id == tempMapId) {
            taskInfo = val;
            return true;
          } else {
            return false;
          }
        });
      }
      return taskInfo;
    };

    const setFloorTaskInfo = (val: CleanTaskingInfo) => {
      const taskId = getMyMapTaskId(val);
      if (taskId) {
        const floorTaskStatus = val.result?.floor_task_status?.run_state;
        if (
          floorTaskStatus == ConstantsType.floor_task_status_ing_1 ||
          floorTaskStatus == ConstantsType.floor_task_status_pause_4
        ) {
          const curTaskInfo = getMyMapFloorRunTaskInfo(val);
          if (curTaskInfo) {
            setAllFloorLaneMap(curTaskInfo?.path_list);
            setFloorLaneMap(curTaskInfo?.path_list);
          } else {
            webGlManager.clearRunningTaskPaths();
          }
        } else {
          webGlManager.clearRunningTaskPaths();
        }
      }
    };

    const setCombineTaskInfo = (val: CleanTaskingInfo) => {
      let taskInfo: TaskInfo | undefined | null;
      if (val.result?.clean_task_status?.child_task_list?.length > 0) {
        taskInfo = getTaskNameForId(
          val.result?.clean_task_status?.task_info_id
        );
        if (
          val.result?.clean_task_status?.task_status ==
          ConstantsType.task_status_over_2
        ) {
          webGlManager.clearRunningTaskPaths();
        } else {
          setAllRunningLaneMap(val.result?.clean_task_status?.child_task_list);
          setRunningLaneMap(val.result?.clean_task_status?.child_task_list);
        }
      }
      return taskInfo;
    };

    const clearAllTaskPaths = (val: CleanTaskingInfo) => {
      if (
        !(val.result?.clean_task_status?.child_task_list?.length > 0) &&
        !(val.result?.floor_task_status?.composite_task_list?.length > 0)
      ) {
        webGlManager.clearRunningTaskPaths();
      }
    };

    const lastRunningLaneIndex = -1;
    const delTaskIngInfo = (val: CleanTaskingInfo) => {
      cleanTaskingInfo.value = val;
      const taskInfo = setCombineTaskInfo(val);
      setFloorTaskInfo(val);
      context.emit("listen-task", val, taskInfo);
      clearAllTaskPaths(val);
    };

    const getTaskIngInfo = () => {
      if (vin.value) {
        Api.getTaskIngInfo(vin.value).then((val) => {
          delTaskIngInfo(val);
        });
      }
    };

    let taskIngInfoInterval: number;
    const pollTaskIngInfo = () => {
      clearInterval(taskIngInfoInterval);
      taskIngInfoInterval = setInterval(() => {
        getTaskIngInfo();
      }, 5000);
    };

    const getMapId = () => {
      if (vin.value) {
        Api.getCurrentMapId(vin.value).then((val) => {
          mapId.value = val;
        });
      }
    };

    let carCurtMapIdInterval: number;
    const pollCarCurrentMapId = () => {
      clearInterval(carCurtMapIdInterval);
      carCurtMapIdInterval = setInterval(() => {
        getMapId();
      }, 5000);
    };

    watch(
      vin,
      () => {
        getMapId();
        getTaskIngInfo();
      },
      { immediate: true }
    );

    watch(
      mapId,
      () => {
        getCurMapDetail();
      },
      { immediate: true }
    );

    watch(myMapId, () => {
      getMyMapDetail();
    });

    watch([mapId, myMapId], () => {
      if (myMapId.value) {
        if (mapId.value == myMapId.value) {
          webGlManager.setShowCarIcon(true);
        } else {
          webGlManager.setShowCarIcon(false);
        }
      } else {
        webGlManager.setShowCarIcon(true);
      }
    });

    watch(rotateAngle, () => {
      webGlManager.setRotateAngle(rotateAngle.value);
    });

    watch(scale, () => {
      webGlManager.setScale(scale.value);
      context.emit("listen-scale", scale.value);
    });

    watch([translateX, translateY], () => {
      webGlManager.setTranslateVal(translateX.value, translateY.value);
    });

    watch(
      showMapVirtualWalls,
      () => {
        webGlManager.setShowVirtualWalls(showMapVirtualWalls.value);
      },
      { immediate: true }
    );

    watch(
      showFunctionPoints,
      () => {
        webGlManager.setShowFunctionPoints(showFunctionPoints.value);
      },
      { immediate: true }
    );

    watch(
      showFunctionAreas,
      () => {
        webGlManager.setShowFunctionAreas(showFunctionAreas.value);
      },
      { immediate: true }
    );

    onMounted(() => {
      setTimeout(() => {
        webGlManager.init();
        pollTaskIngInfo();
        pollCarCurrentMapId();
        pollVehiclePosition();
        getFallPrevention();
      });
    });

    onUnmounted(() => {
      webGlManager.destroyAll();
      clearInterval(taskIngInfoInterval);
      clearInterval(carCurtMapIdInterval);
      clearInterval(vehiclePositionInterval);
    });

    let lastClickCanvas: number;
    const clickCanvasDiv = () => {
      const now = new Date().getTime();
      if (now - lastClickCanvas < 200) {
        // 200毫秒内判断为双击事件
        lastClickCanvas = 0;
        resetTranslateMinScale();
      } else {
        lastClickCanvas = now;
      }
    };

    return () => {
      return <div id={mapDivId} onClick={clickCanvasDiv}></div>;
    };
  },
});
